import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import {
    MyFarmPageLayout,
    MockDataLabel,
    ContractStatusBadge,
    EntityFeed,
    DocumentPreview,
    PageLoadOverlay,
    Paginator,
    type EntityFeedItem,
    type EntityFeedGroup,
    type MockEntity,
    type EntityType,
} from '@hofsync/design-system';

import { useFeatureFlags } from '../../contexts/FeatureFlagContext';
import { useSearch } from '../../contexts/SearchContext';
import { useEntities, useEntitiesByIds, useEntityFacet, useFarmBootstrap, useFarmLenses, useLenseCounts } from '../../hooks/useTwin';
import { usePageLoadTelemetry } from '../../hooks/usePageLoadTelemetry';
import type { LenseConfig, ReverseRelation } from '../../services/twinService';
import { useTwinSSE } from '../../hooks/useTwinSSE';
import { useSourceSystems } from '../../hooks/useSourceSystems';
import { useTenantScopeId } from '../../hooks/useTenantScopeId';
import { readStoredLense, resolveStoredLense, writeStoredLense } from './lensePreference';
import { MOCK_CONTRACTS } from '../../mocks/betrieb-mueller/twin';
import { getEntitiesByType, getEntityById } from '../../mocks/betrieb-mueller/entities';
import { resolveIcon, CompositeIcon, lensesToLenseItems, computeMockLenseCounts } from './farmCategoryConfig';
import { resolveEntityTitle, resolveDisplayTemplate } from './displayName';
import ObjectView from './object/ObjectView';
import TermPage from './object/sources/TermPage';
import MappingEditorPanel from './object/sources/MappingEditorPanel';
import GroupEditorPanel from './object/sources/GroupEditorPanel';
import type { EditorIntent } from './object/sources/SourcesPanel';
import type { SourceRowDto } from '../../services/sourcesService';
import { renderAliasMatch } from './AliasMatchNote';
import { withAsOfParam } from './object/asof';
import { ALIAS_ATTRIBUTE } from './object/nameAliases';
import { usePreviewState } from '../documents/usePreviewState';
import { OHNE_JAHR, hasYearAxis, withYearParam, yearBuckets } from './yearAxis';
import { YearAxisControl } from './YearAxisControl';
import './FarmPage.css';

// --- Formatting helpers ---

function formatQty(value: number | undefined | null, unit: string | undefined | null): string {
    if (value == null) return '\u2014';
    return `${value.toLocaleString('de-DE')} ${unit ?? ''}`;
}

function formatDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// --- Live entity row helpers ---

/**
 * Resolve the stable identifier for a live entity row. The ontology's
 * declared ``primary_key`` (e.g. ``organization_id``) is preferred, but
 * the UDB read path returns ``entity_id`` as the only universally-set
 * identifier — the parquet legacy path populated both. Without the
 * ``entity_id`` fallback every UDB-backed row collapses to ``""`` which
 * (a) breaks the EntityFeed accordion (all tiles share the same id and
 * expand together — observed in prod 2026-04-30 on tenant
 * ``7552aac2…``) and (b) shadows the document-link bulk-fetch keying.
 */
export function resolveEntityRowId(row: Record<string, unknown>, primaryKey?: string | null): string {
    const fromPk = primaryKey ? row[primaryKey] : undefined;
    if (fromPk != null && fromPk !== '') return String(fromPk);
    const fromEntityId = row.entity_id;
    if (fromEntityId != null && fromEntityId !== '') return String(fromEntityId);
    const fromId = row.id;
    return fromId != null ? String(fromId) : '';
}

// --- Config-driven filter helpers ---

/**
 * Issue #593 (2026-05-02): legacy ``Alle …`` sub-lense IDs that the
 * lenses.yaml dropped. Stale URLs and bookmarks carrying these in
 * ``?sub=`` resolve to the parent's all-view (``?sub`` ignored) so we
 * never serve a 404 for a link that worked yesterday.
 *
 * Exported for unit testing — the membership check below runs on
 * every render so the set is small and constant; the export lets
 * ``FarmPage.test.tsx`` lock the membership without driving a full
 * Router + QueryClient render.
 */
export const STALE_ALL_SUB_IDS = new Set([
    'all-organizations',
    'all-contracts',
    'all-animals',
    'all-fields',
    'all-persons',
]);

/**
 * Resolve the JSONB-containment filter for a parent lense + selected
 * sub-lense. Exported for unit testing — the implementation is small
 * but drives the FE's "parent all-view vs sub-lense filtered view"
 * distinction (issue #593 §1).
 */
export function resolveFilters(
    lense: LenseConfig | undefined,
    subcategoryId: string | null,
): Record<string, string> | undefined {
    // Issue #593: when no sub-lense is selected, the parent shows the
    // unfiltered all-view — return ``undefined`` so the backend skips
    // the JSONB containment clause and the FE's ``filterEntities``
    // is a no-op for the demo path.
    if (!lense || !subcategoryId) return undefined;
    const child = (lense.children ?? []).find(c => c.id === subcategoryId);
    if (child?.filter_cycle_season) {
        // „Aktuelle / Alle Flächen“: a server-computed field set, sent as the reserved
        // ``_cycle_season`` parameter beside any containment filter (never a JSONB key).
        return { ...(child.filter ?? {}), [CYCLE_SEASON_PARAM]: child.filter_cycle_season };
    }
    return child?.filter;
}

/** The reserved list parameter a ``filter_cycle_season`` child travels as (backend ``lense_cycle_season``). */
export const CYCLE_SEASON_PARAM = '_cycle_season';

/**
 * Resolve the ``filter_missing`` predicate for a parent lense + selected
 * sub-lense (issue #669). Returns the list of attribute keys that must
 * be missing/empty on each row, or ``undefined`` when the active sub-
 * lense has no missing predicate. Composes with ``resolveFilters`` —
 * a sub-lense can carry both, and the backend ANDs them.
 */
export function resolveFilterMissing(
    lense: LenseConfig | undefined,
    subcategoryId: string | null,
): string[] | undefined {
    if (!lense || !subcategoryId) return undefined;
    const child = (lense.children ?? []).find(c => c.id === subcategoryId);
    return child?.filter_missing && child.filter_missing.length > 0
        ? child.filter_missing
        : undefined;
}

/**
 * Map a selected ``?year=`` value to the server filter it contributes for a
 * year-keyed lens (#1934). A real year → a JSONB containment predicate on the
 * lens's ``year_attribute`` (``{crop_year: '2024'}``, the SAME generic filter
 * path ``?ff=`` compiles to); the ``OHNE_JAHR`` sentinel → a ``_missing``
 * predicate on that attribute (JSONB containment can't express "key absent");
 * no year, or a lens with no ``year_attribute`` → neither. Exported pure so
 * the URL-grammar → filter mapping is unit-tested (FarmPage.test.tsx),
 * mirroring the ``resolveFilters``/``resolveFilterMissing`` precedent.
 */
export function resolveYearFilter(
    yearAttribute: string | undefined,
    year: string | null,
): { filter?: Record<string, string>; missing?: string } {
    if (!yearAttribute || !year) return {};
    if (year === OHNE_JAHR) return { missing: yearAttribute };
    return { filter: { [yearAttribute]: year } };
}

/**
 * Whether a ``LensePanel.onCategorySelect(categoryId, sub)`` is a real lens
 * SWITCH vs a sub-view selection within the CURRENT lens. ``onCategorySelect``
 * fires for both — a parent-lens click and a sub-lens click both pass the
 * PARENT's ``categoryId`` — so the year axis (per-lens, #1934) must survive a
 * sub-view change and only be dropped when the lens itself changes.
 * ``currentLenseId`` is the resolved active lens id (``effectiveCategory``);
 * ``null`` (cold entry, before the lens registry resolves) always reads as a switch.
 * Exported pure so the keep-across-``?sub=`` contract is unit-tested.
 */
export function isLensSwitch(currentLenseId: string | null | undefined, categoryId: string): boolean {
    return categoryId !== currentLenseId;
}

function resolveFeedGroups(
    lense: LenseConfig | undefined,
    subcategoryId: string | null,
): EntityFeedGroup[] | undefined {
    if (!lense?.feed_groups) return undefined;
    const filters = resolveFilters(lense, subcategoryId);
    return filters ? undefined : lense.feed_groups;
}

// --- Config-driven item icon resolver ---

function resolveItemIcon(
    lense: LenseConfig | undefined,
    item: Record<string, unknown>,
): { icon: React.ReactNode; variant: string } | undefined {
    if (!lense?.item_icons) return undefined;
    const { key, map, composite } = lense.item_icons;
    const value = String(item[key] ?? '');
    const entry = map[value];
    if (!entry) return undefined;
    const icon = composite === false
        ? resolveIcon(entry.icon)
        : <CompositeIcon base={lense.icon} badge={entry.icon} />;
    return { icon, variant: entry.variant ?? 'info' };
}

// --- Config-driven feed display resolver ---

function resolvePath(path: string, data: Record<string, unknown>): unknown {
    return path.split('.').reduce<unknown>((obj, key) => {
        if (obj != null && typeof obj === 'object') return (obj as Record<string, unknown>)[key];
        return undefined;
    }, data);
}

function filterEntities(
    entities: MockEntity[],
    filters: Record<string, string> | undefined,
    filterMissing?: string[],
    q?: string | null,
): MockEntity[] {
    const hasContainment = filters && Object.keys(filters).length > 0;
    const hasMissing = filterMissing && filterMissing.length > 0;
    const search = q && q.trim().length > 0 ? q.trim().toLowerCase() : null;
    if (!hasContainment && !hasMissing && !search) return entities;
    return entities.filter(e => {
        if (hasContainment) {
            // A reserved ``_``-parameter (``_cycle_season``) is resolved by the backend; the demo pool
            // carries no crop cycles, so it is not a property test here.
            const ok = Object.entries(filters!).every(
                ([k, v]) => k.startsWith('_') || e.properties[k] === v,
            );
            if (!ok) return false;
        }
        if (hasMissing) {
            // Issue #669 — each listed key must be missing or empty
            // string. Same predicate the backend applies via UDB.
            const props = e.properties as Record<string, unknown>;
            const allMissing = filterMissing!.every(
                k => props[k] == null || props[k] === '',
            );
            if (!allMissing) return false;
        }
        if (search) {
            // Issue #637 — case-insensitive substring on entity_id +
            // every string-valued property. Mirrors the backend
            // ``entity_id ILIKE :q OR attributes::text ILIKE :q`` so
            // demo and live behave the same way.
            const haystackParts: string[] = [e.id, e.name];
            for (const value of Object.values(e.properties)) {
                if (typeof value === 'string') haystackParts.push(value);
                else if (typeof value === 'number' || typeof value === 'boolean') {
                    haystackParts.push(String(value));
                }
            }
            const haystack = haystackParts.join('\n').toLowerCase();
            if (!haystack.includes(search)) return false;
        }
        return true;
    });
}

/**
 * Synthetic nested party shape produced from the organization lookup so
 * the agreement lense's `feed_display.subtitle` template
 * (`"{supplier.short_name} ↔ {buyer.short_name}"`) keeps working
 * post-Phase-H. The retired `/twin/contracts` route used to inject
 * full `PartySummary` objects via backend enrichment; we rebuild a
 * subset of that shape from the organization entity row here.
 */
type PartyLite = {
    party_id: string;
    name: string;
    short_name: string;
};

function buildPartyLite(
    orgId: string | null,
    orgById: Map<string, Record<string, unknown>> | undefined,
): PartyLite | null {
    if (!orgId || !orgById) return null;
    const org = orgById.get(orgId);
    if (!org) return null;
    const name = typeof org.name === 'string' ? org.name : '';
    const nameShort = typeof org.name_short === 'string' && org.name_short
        ? org.name_short
        : name;
    return {
        party_id: orgId,
        name,
        short_name: nameShort,
    };
}

/**
 * Project one live agreement entity row (post-Phase-H, plain JSON map
 * with supplier_id/buyer_id and ISO-string dates) onto the MockEntity
 * shape the unified entity feed renderer expects.
 *
 * The agreement lense's `feed_display` template references derived
 * fields like `valid_to_display` and pre-formatted quantities — those
 * are computed here so the same template works for live data as it
 * did for the retired `/twin/contracts` route. Title fallback chain:
 * `agreement_number` → `supplier_id` → `agreement_id` (raw row never
 * carries a `name` column post-cutover).
 *
 * `orgById` is the organization-id → row map built from
 * `useEntities('organization')`. When supplied, the projection
 * synthesises nested `supplier`/`buyer` objects with `short_name` so
 * the subtitle template `{supplier.short_name} ↔ {buyer.short_name}`
 * resolves to the human-readable Genossenschaft / partner name. When
 * absent (org lookup still loading, or org missing from the response),
 * the fields fall back to empty strings — which produces a degraded
 * but not broken subtitle.
 *
 * Exported for unit testing — kept at module scope so the projection
 * logic is independent of React render context.
 */
export function projectAgreementRow(
    row: Record<string, unknown>,
    id: string,
    orgById?: Map<string, Record<string, unknown>>,
): MockEntity {
    const validFromVal = row.valid_from;
    const validToVal = row.valid_to;
    const validFrom = typeof validFromVal === 'string' ? validFromVal : null;
    const validTo = typeof validToVal === 'string' ? validToVal : null;
    const qtyUnit = typeof row.quantity_unit === 'string' ? row.quantity_unit : null;
    const contractedQ = typeof row.contracted_quantity === 'number' ? row.contracted_quantity : null;
    const totalDeliveredQ = typeof row.total_delivered_qty === 'number' ? row.total_delivered_qty : null;
    const outstandingQ = typeof row.outstanding_qty === 'number' ? row.outstanding_qty : null;
    const fulfillment = typeof row.fulfillment_pct === 'number' ? row.fulfillment_pct : null;
    const supplierId = typeof row.supplier_id === 'string' ? row.supplier_id : null;
    const buyerId = typeof row.buyer_id === 'string' ? row.buyer_id : null;
    const supplier = buildPartyLite(supplierId, orgById);
    const buyer = buildPartyLite(buyerId, orgById);
    // No placeholder synthesis (design principle 2 / bug B4): a missing
    // date yields a missing key — collapse-empty templates and the
    // property grid's empty-handling do the rest. "— – —" must never be
    // manufactured as a value.
    const deliveryPeriod = validFrom && validTo
        ? `${formatDate(validFrom)} – ${formatDate(validTo)}`
        : validFrom
            ? `ab ${formatDate(validFrom)}`
            : validTo
                ? `bis ${formatDate(validTo)}`
                : undefined;
    const properties: Record<string, unknown> = {
        ...row,
        valid_to_display: validTo ? formatDate(validTo) : undefined,
        delivery_period: deliveryPeriod,
        contracted_quantity: contractedQ != null ? formatQty(contractedQ, qtyUnit) : row.contracted_quantity,
        total_delivered_qty: totalDeliveredQ != null ? formatQty(totalDeliveredQ, qtyUnit) : row.total_delivered_qty,
        outstanding_qty: outstandingQ != null ? formatQty(outstandingQ, qtyUnit) : row.outstanding_qty,
        fulfillment_pct: fulfillment != null ? `${fulfillment.toLocaleString('de-DE')}%` : row.fulfillment_pct,
        // Synthetic nested objects so feed_display.subtitle resolves
        // {supplier.short_name} / {buyer.short_name} when the org lookup
        // has the party; omitted entirely otherwise (an all-empty object
        // rendered as a blank "Supplier" grid row).
        supplier: supplier ?? undefined,
        buyer: buyer ?? undefined,
    };
    // Title fallback chain prefers the human-readable supplier short_name
    // (now that we have the org lookup) over the raw supplier_id UUID.
    const name = String(
        row.agreement_number
        ?? supplier?.short_name
        ?? supplier?.name
        ?? supplierId
        ?? id
        ?? '',
    );
    return {
        id,
        name,
        type: 'agreement' as unknown as EntityType,
        properties,
        documents: [],
    };
}

// --- Main component ---

const FarmPage: React.FC = () => {
    const { t } = useTranslation();
    const { hasFlag } = useFeatureFlags();
    const useLiveData = hasFlag('twin.entities');
    const [searchParams, setSearchParams] = useSearchParams();
    // FR-018.7 — the Sources section's open intents. The mapping editor (T12) and the
    // shared-group panel (T14) are hosted here and read these.
    const [sourcesIntent, setSourcesIntent] = React.useState<EditorIntent | null>(null);
    const [groupRow, setGroupRow] = React.useState<SourceRowDto | null>(null);

    // SSE for live updates
    useTwinSSE({ enabled: useLiveData });

    // Build lense panel categories from API config.
    // Phase 07-6 kill-list #1 (2026-05-01): single bootstrap call
    // returns lenses + counts + (optional) entities — collapses the
    // 3-RTT cold-load waterfall to 1 RTT. The bootstrap also seeds
    // the per-entity-list React Query cache so a downstream
    // ``useEntities(entityType)`` reads from cache instead of firing
    // a 4th network call.
    //
    // Demo mode (codex review on PR #594, P2): skip the bootstrap
    // entirely and just fetch lenses. ``get_lense_counts`` is
    // expensive server-side, and in demo mode we throw the result
    // away in favour of ``computeMockLenseCounts`` below — paying
    // for that query would regress demo cold-load time for no
    // benefit. ``useFarmLenses()`` reads the tenant-agnostic ontology
    // config and is cheap.
    //
    // Bug 1 (2026-05-01): badge counts must come from the same
    // entity pool the entity-list path renders. In demo mode the
    // list path renders mock data (most types via
    // ``getEntitiesByType()``, agreements via ``MOCK_CONTRACTS``), so
    // the badges have to be derived from those pools too.
    // Issue #637 — wire the global header search through the
    // digital-twin entity feed and lense-sidebar tallies. Mirrors the
    // documents pattern (``DocumentsPage.tsx`` reads ``useSearch()`` and
    // forwards as ``search``). React state only — no URL sync, just
    // like documents — so a navigate-away-and-back doesn't strand the
    // user inside a stale search.
    const { searchQuery } = useSearch();
    const trimmedSearch = searchQuery.trim();
    const activeSearch = trimmedSearch.length > 0 ? trimmedSearch : null;

    // HOF-106 — which tenant's remembered lens applies. Withheld (``resolved``
    // false) while auth is in flight; see the #2108 stamping effect below for
    // why that distinction has to reach this page rather than being flattened
    // to null. Mechanics are HOF-101's, reused as built.
    const { tenantId, resolved: tenantResolved } = useTenantScopeId();
    // Read once per tenant. Deliberately NOT in a lazy ``useState`` initializer:
    // that idiom fits a preference whose namespace is known at mount, and this
    // one's is not — the tenant arrives with ``/auth/me``. Re-reading when the
    // scope changes is what makes „Anzeige als" hand over the right farm's lens.
    const storedLenseRaw = useMemo(
        () => (tenantResolved ? readStoredLense(tenantId) : null),
        [tenantResolved, tenantId],
    );

    const lenseSlug = searchParams.get('lense') ?? undefined;
    // HOF-106 — ask the bootstrap for the lens we are actually going to land on.
    // Without this the cold visit costs two lens reads: the bootstrap seeds the
    // DEFAULT lens's entity cache, the stamp below immediately re-points the
    // page at the remembered one, and the reader watches the feed load twice —
    // a spinner introduced by the very feature meant to save them a click. The
    // slug is passed unvalidated on purpose: the registry has not been fetched
    // yet at this line, and a stale slug is a path the backend already handles
    // (it falls back to the first lense and echoes it as ``data.lense``, the
    // #594-round-3 behaviour ``effectiveCategory`` below relies on).
    const { data: bootstrapData, isLoading: bootstrapLoading } = useFarmBootstrap(
        lenseSlug ?? storedLenseRaw ?? undefined,
        useLiveData,
    );
    // #3385 — the same source-system registry the object view's badges use.
    // Without it the feed's "gefunden über <System>-Bezeichnung" note stays
    // pinned to the bundled five, so one alias would be attributed in the
    // object view and read as a nameless "frühere Bezeichnung" in the feed.
    const sourceSystems = useSourceSystems();
    // Issue #637: when the user types in the header search, bootstrap
    // counts (which are search-less by design — bootstrap is the
    // cold-load entry point) are no longer authoritative. Fire a
    // dedicated ``useLenseCounts(q)`` so the sidebar badges match the
    // search-filtered feed. Disabled when there is no search so the
    // bootstrap path stays the single source for the unfiltered case.
    const searchedLenseCounts = useLenseCounts(useLiveData && activeSearch !== null, activeSearch);
    // Demo mode falls back to the lenses-only fetch (cheap, no
    // tenant counts query). Live mode reads lenses out of the
    // bootstrap response — calling ``useFarmLenses()`` here too
    // would issue a redundant ``/twin/lenses`` round trip on every
    // FarmPage mount (codex review on PR #594, P2).
    const { data: demoLensesData } = useFarmLenses(!useLiveData);
    const lensesData = useLiveData ? bootstrapData?.lenses : demoLensesData;
    // Issue #637: when a header search is active prefer the dedicated
    // counts query (which carries ``q``) over the search-less bootstrap
    // counts so the sidebar tallies stay consistent with the feed.
    const liveLenseCounts = activeSearch !== null
        ? searchedLenseCounts.data
        : bootstrapData?.counts;
    const lenseCounts = useMemo(() => {
        if (useLiveData) return liveLenseCounts;
        if (!lensesData) return undefined;
        // Issue #637: pass the global search through so demo-mode
        // sidebar tallies stay consistent with the search-filtered
        // entity list.
        return computeMockLenseCounts(lensesData, (lense) => {
            if (lense.entity_type === 'agreement') {
                // Mirror the entity-list-path projection: only properties
                // matter for child-filter matching, so the cheap shape
                // suffices (full projection is in ``filteredEntities``).
                return MOCK_CONTRACTS.map((c) => ({
                    id: c.agreement_id,
                    name: c.agreement_number ?? '',
                    type: 'agreement' as MockEntity['type'],
                    properties: c as unknown as Record<string, unknown>,
                    documents: [],
                }));
            }
            return getEntitiesByType(lense.entity_type as MockEntity['type']);
        }, activeSearch);
    }, [useLiveData, liveLenseCounts, lensesData, activeSearch]);
    const farmCategories = useMemo(
        () => (lensesData && lenseCounts) ? lensesToLenseItems(lensesData, lenseCounts) : [],
        [lensesData, lenseCounts],
    );

    // URL is the single source of truth for navigation state
    const selectedCategory = searchParams.get('lense');
    const rawSelectedSubcategory = searchParams.get('sub');
    // Issue #593 (2026-05-02): treat stale ``?sub=all-*`` URLs as
    // unselected so old bookmarks land on the parent's all-view rather
    // than 404'ing or showing an empty list.
    const selectedSubcategory = rawSelectedSubcategory && STALE_ALL_SUB_IDS.has(rawSelectedSubcategory)
        ? null
        : rawSelectedSubcategory;
    const itemParam = searchParams.get('item');
    // Farm twin-explorer (design §3.2): ``?type=`` overrides the lens
    // entity_type for the object view — how headless types (reached by
    // traversing relations) render without a lens of their own.
    const typeParam = searchParams.get('type');
    // ``?ff=<field>:<id>`` — FK list filter from a reverse relation's
    // "alle anzeigen" (design §3.4). Compiles to a JSONB containment
    // predicate server-side like any lens child filter.
    const ffParam = searchParams.get('ff');
    // ``?asof=<ISO>`` — per-item as-of navigation on the object view (#1913
    // C). Query-time + deep-linkable, mirroring Deckungsbeitrag's ``?period=``
    // (principle 10): the object view reconstructs the item's state at this
    // instant client-side from the fetched transition series. Absent = live.
    const asofParam = searchParams.get('asof');
    // ``?term=<crop>`` — FR-018.7 §8 vocabulary-term page; only meaningful under the crop_cycles lens.
    const termParam = searchParams.get('term');
    // ``?year=<Erntejahr>`` — the crop-cycle year axis (#1934). A dedicated
    // query axis like ``?asof=`` (not a ``?sub=`` config axis): valid only
    // while the active lens declares a ``year_attribute``. The value is a raw
    // year, or the ``OHNE_JAHR`` sentinel for the null-year bucket. Absent =
    // all years.
    const yearParam = searchParams.get('year');

    // Codex review on PR #638 (P3, 2026-05-02): canonicalise the URL
    // by removing the stale ``?sub=all-*`` param entirely. Without
    // this, the param survives in ``searchParams``; later URL-
    // preserving actions (clicking an item — ``handleExpandedChange``
    // copies ``prev`` and only mutates ``item`` — or copy-link from
    // the address bar) keep propagating the dead sub-lense ID.
    // Replace-history so the bad URL doesn't clutter the back-button
    // stack. Runs once per stale-URL detection because the next
    // render sees ``rawSelectedSubcategory === null`` and skips the
    // branch.
    React.useEffect(() => {
        if (rawSelectedSubcategory && STALE_ALL_SUB_IDS.has(rawSelectedSubcategory)) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('sub');
                return next;
            }, { replace: true });
        }
    }, [rawSelectedSubcategory, setSearchParams]);

    // #2108 — the /farm starting page is retired. A bare /farm (no
    // ``?lense=``) canonicalises to the FIRST lens in the order the lens
    // registry serves them, via a replace-navigation so bookmarks and the
    // left-panel "Mein Betrieb" click land directly in the content
    // without stacking a history entry. The target is resolved from the
    // registry (``lensesData[0]``) — never a hardcoded lens id. An empty
    // registry skips the redirect; the entity list below renders its
    // graceful empty state instead.
    //
    // HOF-106 — the target is now the REMEMBERED lens when there is a valid one,
    // and the first registry-served lens otherwise. Precedence: an explicit
    // ``?lense=`` (a statement about THIS visit) beats the stored preference,
    // which beats first-served. The stored slug is validated against the same
    // registry the URL slug is checked against, so a lens the tenant's ontology
    // no longer serves falls through instead of resurrecting a dead slug.
    //
    // And equally: not before the TENANT is known. The preference cannot be read
    // until ``/auth/me`` lands, and stamping first-served in the meantime would
    // write a parameter this page then treats as an explicit deep link — the
    // arriving preference would lose to the page's own guess, on every cold
    // visit. ``ProtectedLayout`` already holds the route until auth settles, so
    // in the app this gate rarely fires; it is what keeps the precedence a
    // property of the page rather than of who happens to mount it.
    const preferredLense = useMemo(
        () => resolveStoredLense(storedLenseRaw, lensesData),
        [storedLenseRaw, lensesData],
    );
    React.useEffect(() => {
        if (lenseSlug) return;
        if (!tenantResolved) return;
        const target = preferredLense ?? lensesData?.[0]?.id;
        if (!target) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('lense', target);
            return next;
        }, { replace: true });
    }, [lenseSlug, tenantResolved, preferredLense, lensesData, setSearchParams]);

    // Derive effective lense selection from config — default to first
    // registry-served lens (covers the render window before the #2108
    // canonicalisation effect above lands). Codex review on PR #594
    // round 3 (P2): when the URL slug is stale (e.g. ``?lense=banana``),
    // the backend bootstrap already falls back to the first lense and
    // echoes its slug as ``data.lense``; honour that here so
    // ``selectedLense`` resolves and the seeded entity-list cache is
    // consumed. Without this, bootstrap returns useful entities but the
    // FE renders an empty panel because ``selectedLense`` is undefined.
    const matchedSlug = selectedCategory && lensesData?.some(l => l.id === selectedCategory)
        ? selectedCategory
        : null;
    // HOF-106 — the remembered lens sits between the URL slug and the defaults,
    // and is already registry-validated. It is stated here rather than left to
    // the bootstrap echo because demo mode has no bootstrap at all, and because
    // the render window before the stamping effect lands should show the lens
    // the reader is about to get, not the one they are not.
    // ``lenseSlug ? null : preferredLense`` and not a bare ``preferredLense``: an
    // explicit parameter suppresses the preference whether or not it RESOLVES,
    // which is HOF-101's own semantics (``katParam || storedKatId``). A stale
    // bookmark (``?lense=bananas``) must fall through to the backend echo and
    // then first-served, exactly as it did before this feature — letting the
    // preference answer there would render a lens the URL does not name, and the
    // stamping effect leaves an explicit parameter alone, so nothing would ever
    // reconcile the two. Codex round 1, P2.
    const effectiveCategory =
        matchedSlug ?? (lenseSlug ? null : preferredLense) ?? bootstrapData?.lense ?? lensesData?.[0]?.id ?? null;
    const selectedLense = lensesData?.find(l => l.id === effectiveCategory);
    // Issue #593: a missing ``?sub`` (or one we just rewrote to null)
    // means "show the parent's unfiltered all-view" — no implicit
    // fallback to the first child. The breadcrumb collapses to
    // ``Mein Betrieb › Organisationen`` (LenseNavItem keeps the parent
    // selected via ``isActiveItem = selectedItem && !selectedSubItem``).
    const effectiveSubcategory = selectedSubcategory;

    // Cross-lense deep-link (demo mode): when item points to a MOCK
    // entity in a different lense, update the URL. Live mode handles
    // cross-type navigation explicitly via ``?type=``.
    React.useEffect(() => {
        if (useLiveData || !itemParam || !lensesData) return;
        const entity = getEntityById(itemParam);
        if (!entity) return;
        const targetLense = lensesData.find(l => l.entity_type === entity.type);
        if (!targetLense || targetLense.id === effectiveCategory) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('lense', targetLense.id);
            next.delete('sub');
            return next;
        }, { replace: true });
    }, [useLiveData, itemParam, lensesData, effectiveCategory, setSearchParams]);

    // Config-driven filters
    const activeFilters = useMemo(
        () => resolveFilters(selectedLense, effectiveSubcategory),
        [selectedLense, effectiveSubcategory],
    );

    // Issue #669: ``filter_missing`` predicate for the active sub-lense
    // (Personen/Ohne Rolle, Organisationen/Ohne Typ).
    const activeFilterMissing = useMemo(
        () => resolveFilterMissing(selectedLense, effectiveSubcategory),
        [selectedLense, effectiveSubcategory],
    );

    const activeGroups = useMemo(
        () => resolveFeedGroups(selectedLense, effectiveSubcategory),
        [selectedLense, effectiveSubcategory],
    );

    const isAgreementView = selectedLense?.entity_type === 'agreement';

    // Live data hook — Phase I step 4 (2026-04-29): the agreement view used
    // to call the dedicated useContracts/`/api/v1/twin/contracts` (list)
    // route. That route reads stale parquet post-Phase-H, and step 5 wants
    // it deleted. The agreement lense now uses the same useEntities path
    // as every other entity_type. Lense `feed_display.title` resolves
    // `{agreement_number}` directly off the entity row.
    //
    // Issue #593 (2026-05-02): opt this call into server-side pagination
    // (``paginated: true``) and pass ``activeFilters`` so the backend
    // applies the sub-lense's JSONB containment server-side. Two wins:
    // (a) prod tenants with hundreds of persons or organisations no
    // longer ship the full pool to the FE every render — ``pageSize=14``
    // mirrors ``useDocuments``; (b) the sub-lense filter and the
    // pagination ``ORDER BY display_name ASC, entity_id ASC`` (UDB
    // default, see ``entity_store.py::_DISPLAY_NAME_ORDER_BY``) live
    // together in the same query, so paginating across a filtered set
    // stays stable. ``filterEntities`` below is now a no-op for live
    // data (filters round-trips server-side); kept on the demo path
    // because the mock pool isn't filtered upstream.
    const entityType = selectedLense?.entity_type;

    // Erntejahr axis (#1934): only lenses that declare a ``year_attribute``
    // in lenses.yaml (crop_cycles → ``crop_year`` today) carry a year axis.
    const yearAttribute = selectedLense?.year_attribute;
    // Fetch the distinct year values (+ counts) — live mode only, and only for
    // a year-keyed lens (the hook is disabled otherwise). This drives the
    // CONTROL only (chips + counts); it never gates filtering (see below) nor
    // the primary-load overlay, so the control simply appears once the facet
    // arrives (graceful absence elsewhere).
    const facetQuery = useEntityFacet(
        useLiveData && yearAttribute ? entityType : undefined,
        useLiveData ? yearAttribute : undefined,
    );
    const yearAxis = useMemo(
        () => (facetQuery.data ? yearBuckets(facetQuery.data.items) : []),
        [facetQuery.data],
    );
    // Show the CONTROL only when the axis is worth surfacing: a year-keyed
    // lens, live data, and ≥2 distinct NAMED years (the Ohne-Jahr sentinel
    // doesn't count — a single real year + a null bucket is still one year,
    // #1934 P3-2). This governs chip RENDERING only, never whether the filter
    // applies.
    const showYearAxis = useLiveData && !!yearAttribute && hasYearAxis(yearAxis);
    // The ``?year=`` value in effect — folded into the filter UNCONDITIONALLY,
    // exactly like ``?ff=`` and INDEPENDENT of the control's visibility, so a
    // deep link (``?lense=crop_cycles&year=2023``) filters the feed even while
    // the facet is still loading / empty / errored / single-bucket (#1934 P2).
    // Null when the lens isn't year-keyed (a stale ``?year=`` on another lens
    // is ignored — ``resolveYearFilter`` emits no predicate without an attr).
    const appliedYear = yearAttribute && yearParam ? yearParam : null;

    // ``?ff=`` FK filter (reverse-relation "alle anzeigen") merges into
    // the lens filters as one more containment predicate.
    const ffFilter = useMemo(() => {
        if (!ffParam) return undefined;
        const sep = ffParam.indexOf(':');
        if (sep <= 0) return undefined;
        return { [ffParam.slice(0, sep)]: ffParam.slice(sep + 1) };
    }, [ffParam]);
    // The selected year folds into the SAME server filter path as ``?ff=``:
    // a real year → JSONB containment ``{crop_year: '2024'}``; the Ohne-Jahr
    // bucket → a ``_missing`` predicate (containment can't express key-absent).
    const yearFilterParts = useMemo(
        () => resolveYearFilter(yearAttribute, appliedYear),
        [yearAttribute, appliedYear],
    );
    const effectiveFilters = useMemo(() => {
        const merged = { ...(activeFilters ?? {}), ...(ffFilter ?? {}), ...(yearFilterParts.filter ?? {}) };
        return Object.keys(merged).length > 0 ? merged : undefined;
    }, [activeFilters, ffFilter, yearFilterParts]);
    // Compose the year's Ohne-Jahr ``_missing`` key with any sub-lense
    // ``filter_missing`` (crop_cycles has no sub-lenses today, so no real
    // collision — the append is defensive). ``useEntities``' reset key already
    // includes ``filterMissing``, so a year switch snaps to page 1 for free.
    const effectiveFilterMissing = useMemo(() => {
        if (!yearFilterParts.missing) return activeFilterMissing;
        return [...(activeFilterMissing ?? []), yearFilterParts.missing];
    }, [activeFilterMissing, yearFilterParts]);

    const entitiesQuery = useEntities(
        useLiveData ? entityType : undefined,
        useLiveData ? effectiveFilters : undefined,
        {
            paginated: useLiveData,
            // Issue #669: ``Ohne Rolle`` / ``Ohne Typ`` sub-lenses surface
            // the ``filter_missing`` predicate via the ``_missing`` query
            // param so the backend ANDs it with ``filter_attrs`` server-side.
            // #1934 folds the Ohne-Jahr year bucket in here too.
            filterMissing: useLiveData ? effectiveFilterMissing : undefined,
            // Issue #637: global header search substring. Live mode only —
            // demo mode keeps its in-memory ``filterEntities`` path so the
            // mock pool stays the source of truth.
            q: useLiveData ? activeSearch : undefined,
        },
    );
    const liveEntitiesData = entitiesQuery.data;
    const entitiesLoading = entitiesQuery.isLoading;

    // Issue #643 — page-load telemetry overlay. The primary-load
    // signal is the union of the bootstrap fetch (lenses + counts +
    // seeded entities) and the entities query. Object-view sub-loads
    // (relations, documents, events) intentionally do NOT contribute —
    // only the page-root primary load triggers the overlay or the
    // telemetry POST.
    // Issue #637: include the searched-counts loading state so the
    // overlay reflects the dedicated ``useLenseCounts(q)`` round trip
    // when a search is active (bootstrap counts are stale in that case
    // and aren't re-fetched).
    const primaryLoadActive = useLiveData && (
        bootstrapLoading
        || entitiesLoading
        || (activeSearch !== null && searchedLenseCounts.isLoading)
    );
    const farmView = useMemo(() => {
        const lense = effectiveCategory ?? 'unknown';
        const parts = [
            effectiveSubcategory
                ? `farm:lense=${lense}:sub=${effectiveSubcategory}`
                : `farm:lense=${lense}`,
        ];
        // Issue #637 — flag search presence (not the term itself; the
        // term may be PII) so slow loads under search are correlatable
        // in Kibana, mirroring documents.
        if (activeSearch !== null) parts.push('search=present');
        // Issue #1934 — mark year-filtered loads (token only, not the year
        // value) so an Erntejahr-filtered feed's load time is correlatable.
        if (appliedYear) parts.push('year=present');
        return parts.join(':');
    }, [effectiveCategory, effectiveSubcategory, activeSearch, appliedYear]);
    const pageLoadOverlay = usePageLoadTelemetry({
        page: 'farm',
        view: farmView,
        isLoading: primaryLoadActive,
        pageIndex: entitiesQuery.page,
        pageSize: entitiesQuery.pageSize,
    });

    // Phase I step 4 follow-up: agreement supplier/buyer name lookup.
    // The retired /twin/contracts route enriched supplier_id/buyer_id into
    // nested PartySummary objects (`{supplier.short_name}` etc.) via
    // backend join. Post-cutover the agreement entity row carries only
    // the IDs. We rebuild the nested shape on the FE by joining against
    // the organization entity list, fetched once per agreement-lense view.
    // Organizations change rarely; React Query's default staleness keeps
    // this cheap, and we only fire the fetch when the agreement view is
    // visible AND live data is on (gating prevents an unnecessary call
    // for tenants in demo mode or on other lenses).
    const { data: liveOrgsData } = useEntities(
        useLiveData && isAgreementView ? 'organization' : undefined,
    );
    const orgById = useMemo(() => {
        const map = new Map<string, Record<string, unknown>>();
        if (!liveOrgsData) return map;
        const pk = liveOrgsData.primary_key ?? 'organization_id';
        for (const row of liveOrgsData.entities) {
            // Same primary_key → entity_id → id fallback the entity-list
            // path uses. UDB-backed org rows only carry ``entity_id``,
            // so without this the lookup map stays empty and agreement
            // cards lose supplier/buyer enrichment.
            const id = resolveEntityRowId(row as Record<string, unknown>, pk);
            if (id) {
                map.set(id, row as Record<string, unknown>);
            }
        }
        return map;
    }, [liveOrgsData]);

    const handleCategorySelect = (categoryId: string, subcategoryId: string | null) => {
        // This handler serves BOTH lens selection and sub-view selection, so
        // capture whether the lens itself is changing BEFORE mutating the URL.
        const lensSwitch = isLensSwitch(effectiveCategory, categoryId);
        // HOF-106 — an explicit lens SWITCH is the only thing that writes the
        // preference. A sub-view click is not a statement about which lens to
        // open next time (it arrives here carrying the parent's id, which is
        // what ``isLensSwitch`` exists to tell apart), and merely viewing a
        // ``?lense=`` deep link someone shared is not a choice at all —
        // recording it would let that link silently redefine the reader's own
        // default.
        if (lensSwitch) writeStoredLense(tenantId, categoryId);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('lense', categoryId);
            if (subcategoryId) {
                next.set('sub', subcategoryId);
            } else {
                next.delete('sub');
            }
            next.delete('item');
            next.delete('term'); // a term page belongs to the crop_cycles lens it was opened from
            next.delete('asof'); // as-of is per-item (#1913 C)
            // Year is per-lens (#1934): keep it across a sub-view change (like
            // ``?sub=``/paging), drop it only when the lens actually switches
            // (a year picked on Kulturen is meaningless on Personen).
            if (lensSwitch) next.delete('year');
            return next;
        }, { replace: true });
    };

    const handleExpandedChange = useCallback((id: string | null) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (id) {
                next.set('item', id);
            } else {
                next.delete('item');
            }
            // As-of is per-item (#1913 C) — switching or closing the object
            // view must not carry a stale ``?asof=`` onto a different item.
            next.delete('asof');
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // As-of navigation (#1913 C): set/clear ``?asof=`` — the object view's
    // single source of truth for the reconstructed instant. ``replace`` so
    // stepping through history doesn't stack back-button entries.
    const handleAsOfChange = useCallback((iso: string | null) => {
        setSearchParams((prev) => withAsOfParam(prev, iso), { replace: true });
    }, [setSearchParams]);

    // Year navigation (#1934): set/clear ``?year=`` — the crop-cycle list's
    // Erntejahr axis. ``replace`` so toggling years doesn't stack back-button
    // entries (mirrors ``handleAsOfChange``). The year is KEPT across an item
    // open (``handleExpandedChange`` leaves it) so Zurück restores the
    // filtered list; it is cleared only on a lens switch.
    const handleYearChange = useCallback((year: string | null) => {
        setSearchParams((prev) => withYearParam(prev, year), { replace: true });
    }, [setSearchParams]);

    // Document preview overlay
    const {
        previewState,
        rotating,
        handlePreview,
        handlePreviewClose,
        handlePreviewDownload,
        handlePreviewRotate,
    } = usePreviewState({ t });

    // --- Entity data (live + demo, all lenses including agreement) ---
    // Phase I step 4 (2026-04-29): the agreement view used to take a
    // separate path through useContracts → filteredContracts →
    // contractFeedItems → renderContractList. That path is gone;
    // agreements flow through the same useEntities pipeline as every
    // other entity_type. Demo mode for agreements maps MOCK_CONTRACTS
    // (TwinAgreement[]) to MockEntity-shape on the fly so the unified
    // pipeline can render them too.
    const filteredEntities: MockEntity[] = useMemo(() => {
        if (!selectedLense?.entity_type) return [];

        if (useLiveData && liveEntitiesData) {
            const pk = liveEntitiesData.primary_key;
            const isAgreement = selectedLense.entity_type === 'agreement';
            const normalized: MockEntity[] = liveEntitiesData.entities.map((row) => {
                const id = resolveEntityRowId(row as Record<string, unknown>, pk);
                if (isAgreement) {
                    return projectAgreementRow(row as Record<string, unknown>, id, orgById);
                }
                return {
                    id,
                    name: String(row.name ?? ''),
                    type: selectedLense.entity_type as EntityType,
                    properties: row as Record<string, unknown>,
                    documents: [],
                };
            });
            // Issue #593 (2026-05-02): live ``activeFilters`` are pushed to
            // UDB as JSONB containment; the rows the backend returns are
            // already filtered. ``filterEntities`` would idempotently re-
            // apply them but we skip it to avoid the second pass.
            return normalized;
        }

        if (isAgreementView) {
            // Demo-mode adapter: project TwinAgreement onto MockEntity so
            // the agreement lense renders mock contracts through the same
            // entityFeedItems pipeline as live data.
            const mockAsEntities: MockEntity[] = MOCK_CONTRACTS.map((c) => {
                const props: Record<string, unknown> = {
                    ...(c as unknown as Record<string, unknown>),
                    product: c.product?.name ?? '',
                    supplier_id: c.supplier?.party_id ?? '',
                    buyer_id: c.buyer?.party_id ?? '',
                    valid_to_display: formatDate(c.valid_to),
                    delivery_period: `${formatDate(c.valid_from)} – ${formatDate(c.valid_to)}`,
                    contracted_quantity: formatQty(c.contracted_quantity, c.quantity_unit),
                    total_delivered_qty: formatQty(c.total_delivered_qty, c.quantity_unit),
                    outstanding_qty: formatQty(c.outstanding_qty, c.quantity_unit),
                    fulfillment_pct: `${(c.fulfillment_pct ?? 0).toLocaleString('de-DE')}%`,
                };
                return {
                    id: c.agreement_id,
                    name: c.agreement_number || (c.supplier?.short_name ?? c.supplier?.name ?? ''),
                    type: 'agreement' as unknown as EntityType,
                    properties: props,
                    documents: [],
                };
            });
            return filterEntities(mockAsEntities, activeFilters, activeFilterMissing, activeSearch);
        }

        const pool = getEntitiesByType(selectedLense.entity_type as EntityType);
        return filterEntities(pool, activeFilters, activeFilterMissing, activeSearch);
    }, [selectedLense, isAgreementView, activeFilters, activeFilterMissing, activeSearch, useLiveData, liveEntitiesData, orgById]);

    // --- Ref-name resolution for feed templates (design §3.4, fixes B8) ---
    // A lens template may carry `{ref:<key>}` tokens (e.g. the Kulturen
    // subtitle "Schlag {ref:field_id}"): collect the keys, look up their
    // target type in the merged ontology schema, and batch-resolve the
    // visible page's FK ids to display names in ONE query.
    const refSpecs = useMemo(() => {
        const display = selectedLense?.feed_display;
        const templates = [display?.title, display?.subtitle, display?.meta].filter(
            (v): v is string => typeof v === 'string',
        );
        const keys = new Set<string>();
        for (const template of templates) {
            for (const m of template.matchAll(/\{ref:([^}]+)\}/g)) keys.add(m[1]);
        }
        const specs: Array<{ key: string; target: string }> = [];
        for (const key of keys) {
            const field = selectedLense?.fields?.find((f) => f.id === key);
            if (field?.ref?.entity) specs.push({ key, target: field.ref.entity });
        }
        return specs;
    }, [selectedLense]);
    // One target type per lens is supported (all current templates need
    // exactly one); a second target would need a second query here.
    const refTargetType = refSpecs[0]?.target;
    const refIds = useMemo(() => {
        if (!refTargetType) return [];
        const wanted = refSpecs.filter((spec) => spec.target === refTargetType).map((s) => s.key);
        const ids = new Set<string>();
        for (const entity of filteredEntities) {
            for (const key of wanted) {
                const raw = (entity.properties as Record<string, unknown>)[key];
                if (typeof raw === 'string' && raw) ids.add(raw);
            }
        }
        return [...ids];
    }, [refTargetType, refSpecs, filteredEntities]);
    const { data: refRowsData } = useEntitiesByIds(
        useLiveData && refTargetType ? refTargetType : undefined,
        refIds,
    );
    const refNames = useMemo(() => {
        const map: Record<string, string> = {};
        for (const row of refRowsData?.entities ?? []) {
            const id = resolveEntityRowId(row as Record<string, unknown>, refRowsData?.primary_key);
            if (!id) continue;
            const { title, isFallback } = resolveEntityTitle({
                id,
                name: typeof row.name === 'string' ? row.name : undefined,
                properties: row as Record<string, unknown>,
            });
            if (!isFallback) map[id] = title;
        }
        return map;
    }, [refRowsData]);

    const entityFeedItems: EntityFeedItem[] = useMemo(() => {
        const catIcon = selectedLense ? resolveIcon(selectedLense.icon) : null;
        const display = selectedLense?.feed_display;
        const isAgreementLense = selectedLense?.entity_type === 'agreement';

        return filteredEntities.map((entity) => {
            // Template-rendering source: entity.properties is the raw row
            // (live: backend entity row, demo: TwinAgreement projection).
            // Spreading entity wrapper lets `{name}` and `{id}` work too.
            const data: Record<string, unknown> = {
                ...(entity.properties as Record<string, unknown>),
                ...(entity as unknown as Record<string, unknown>),
            };
            const itemIcon = resolveItemIcon(selectedLense, entity.properties as Record<string, unknown>);
            const iconVariant = itemIcon
                ? itemIcon.variant as EntityFeedItem['iconVariant']
                : 'info' as const;
            const status = (entity.properties as Record<string, unknown>).status;
            const fulfillment = (entity.properties as Record<string, unknown>).fulfillment_pct;
            const fulfillmentPctNum = typeof fulfillment === 'number'
                ? fulfillment
                : typeof fulfillment === 'string' ? parseFloat(fulfillment) || 0 : 0;
            // Collapse-empty template semantics + typed fallback chain
            // (design §3.4) — kills the "—" rows (B1), "bis —" (B3),
            // "— – —" (B4) and the lone "⇔" (B5).
            const { title } = resolveEntityTitle(
                { id: entity.id, name: entity.name, properties: data },
                display?.title,
                { refNames },
            );
            const subtitle = display?.subtitle
                ? resolveDisplayTemplate(display.subtitle, data, { refNames }) ?? undefined
                : undefined;
            const meta = display?.meta
                ? resolveDisplayTemplate(display.meta, data, { refNames }) ?? undefined
                : undefined;
            return {
                id: display?.id_key ? String(resolvePath(display.id_key, data) ?? entity.id) : entity.id,
                group: display?.group_key ? String(resolvePath(display.group_key, data) ?? '') : undefined,
                icon: itemIcon
                    ? <span className="entity-tile__icon-svg">{itemIcon.icon}</span>
                    : catIcon ? <span className="entity-tile__icon-svg">{catIcon}</span> : undefined,
                iconVariant,
                title,
                subtitle,
                meta,
                tags: isAgreementLense && typeof status === 'string'
                    ? <ContractStatusBadge status={status as import('@hofsync/design-system').ContractStatus} suffix={`${Math.round(fulfillmentPctNum)}%`} />
                    : renderAliasMatch(data[ALIAS_ATTRIBUTE], activeSearch, data.name, sourceSystems),
                // No accordion body — a row click navigates to the object
                // view (design §3.2, replaces the cramped inline detail B10).
            };
        });
    }, [filteredEntities, selectedLense, refNames, activeSearch, sourceSystems]);

    // --- Object-view navigation (design §3.2) ---
    const lenseForEntityType = useCallback(
        (type: string) => lensesData?.find((l) => l.entity_type === type),
        [lensesData],
    );
    const hasLensForType = useCallback(
        (type: string) => !!lenseForEntityType(type),
        [lenseForEntityType],
    );

    /** Open the object view for any entity — via its lens when one
     * exists (list context follows), via ``?type=`` otherwise. */
    const handleNavigateToObject = useCallback((targetType: string, targetId: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            const targetLense = lensesData?.find((l) => l.entity_type === targetType);
            if (targetLense) {
                next.set('lense', targetLense.id);
                next.delete('type');
            } else {
                next.set('type', targetType);
            }
            next.set('item', targetId);
            next.delete('term'); // leaving a term page for an object
            next.delete('sub');
            next.delete('ff');
            next.delete('asof'); // as-of is per-item (#1913 C)
            next.delete('year'); // cross-type navigation switches lens (#1934)
            return next;
        });
    }, [setSearchParams, lensesData]);

    /** Reverse relation "alle anzeigen" → the source type's list lens,
     * filtered by the FK (``?ff=<field>:<id>``). */
    const handleShowAllRelation = useCallback((rel: ReverseRelation) => {
        const targetLense = lensesData?.find((l) => l.entity_type === rel.source_type);
        if (!targetLense || !itemParam) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('lense', targetLense.id);
            next.set('ff', `${rel.via_field}:${itemParam}`);
            next.delete('item');
            next.delete('type');
            next.delete('sub');
            next.delete('asof'); // as-of is per-item (#1913 C)
            next.delete('year'); // relation drill switches to the source-type lens (#1934)
            return next;
        });
    }, [setSearchParams, lensesData, itemParam]);

    const handleBackFromObject = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('item');
            next.delete('type');
            next.delete('asof'); // leaving the object view returns to live (#1913 C)
            return next;
        });
    }, [setSearchParams]);

    /** FR-018.7 §8 — a crop cycle's `crop` value → the term page, under the crop_cycles lens. */
    const handleOpenTerm = useCallback((_vocabulary: 'crop', term: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('lense', 'crop_cycles');
            next.set('term', term);
            next.delete('item');
            next.delete('type');
            next.delete('sub');
            next.delete('ff');
            next.delete('asof');
            return next;
        });
    }, [setSearchParams]);

    const handleBackFromTerm = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('term');
            return next;
        });
    }, [setSearchParams]);

    const clearFfFilter = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('ff');
            return next;
        });
    }, [setSearchParams]);

    // The object view renders for ?item= — its entity type is the
    // ``?type=`` override (headless types) or the lens entity_type.
    const objectEntityType = typeParam ?? selectedLense?.entity_type;
    // An editor intent belongs to the object it was opened on: Back/Forward to another object
    // closes it, so a row of object A is never confirmed onto object B (the panel is keyed too).
    const showTermPage = !!termParam && selectedLense?.id === 'crop_cycles';
    const sourcesObjectKey = showTermPage
        ? `crop:${termParam}`
        : `${objectEntityType ?? ''}:${itemParam ?? ''}`;
    React.useEffect(() => {
        setSourcesIntent(null);
        setGroupRow(null);
    }, [sourcesObjectKey]);
    const showObjectView = !showTermPage && !!itemParam && !!objectEntityType;
    const objectMatchesLense = !typeParam || typeParam === selectedLense?.entity_type;

    const renderTermPage = () => {
        if (!termParam) return null;
        const key = `crop:${termParam}`;
        return (
            <>
            <TermPage
                key={`page:${key}`}
                term={termParam}
                onBack={handleBackFromTerm}
                onNavigate={handleNavigateToObject}
                onOpenEditor={setSourcesIntent}
                onOpenGroup={setGroupRow}
                onPreviewDocument={handlePreview}
            />
            <MappingEditorPanel
                key={`editor:${key}`}
                open={!!sourcesIntent}
                intent={sourcesIntent}
                entityType="crop"
                entityId={termParam}
                onClose={() => setSourcesIntent(null)}
                onNavigate={handleNavigateToObject}
                onOpenGroup={(row) => {
                    setSourcesIntent(null);
                    setGroupRow(row);
                }}
            />
            <GroupEditorPanel
                key={`group:${key}`}
                open={!!groupRow}
                row={groupRow}
                entityType="crop"
                entityId={termParam}
                onClose={() => setGroupRow(null)}
                onNavigate={handleNavigateToObject}
            />
            </>
        );
    };

    const renderObjectView = () => {
        if (!itemParam || !objectEntityType) return null;
        const rowFromPage = objectMatchesLense
            ? filteredEntities.find((e) => e.id === itemParam)
            : undefined;
        const objectLense = objectMatchesLense ? selectedLense : undefined;
        const backLabel = selectedLense ? `Zurück zu ${selectedLense.name}` : 'Zurück';
        // Germanize the discriminator enum via the lens feed_groups
        // (agreement_type: sales_agreement → "Verkauf").
        const valueLabels: Record<string, Record<string, string>> | undefined =
            objectLense?.feed_display?.group_key && objectLense.feed_groups?.length
                ? {
                      [objectLense.feed_display.group_key]: Object.fromEntries(
                          objectLense.feed_groups.map((g) => [g.key, g.label]),
                      ),
                  }
                : undefined;
        return (
            <>
            <ObjectView
                key={`page:${objectEntityType}:${itemParam}`}
                entityType={objectEntityType}
                entityId={itemParam}
                row={rowFromPage
                    ? { id: rowFromPage.id, name: rowFromPage.name, properties: { ...(rowFromPage.properties as Record<string, unknown>) } }
                    : undefined}
                fields={objectLense?.fields}
                titleTemplate={objectLense?.feed_display?.title ?? null}
                valueLabels={valueLabels}
                icon={objectLense ? resolveIcon(objectLense.icon) : undefined}
                typeLabel={objectLense?.name}
                live={useLiveData}
                backLabel={backLabel}
                onBack={handleBackFromObject}
                onNavigate={handleNavigateToObject}
                onShowAllRelation={handleShowAllRelation}
                hasLensForType={hasLensForType}
                onPreviewDocument={handlePreview}
                asOf={asofParam}
                onAsOfChange={handleAsOfChange}
                lens={objectLense}
                onOpenSourcesEditor={setSourcesIntent}
                onOpenSourcesGroup={setGroupRow}
                onOpenTerm={handleOpenTerm}
            />
            <MappingEditorPanel
                key={`editor:${objectEntityType}:${itemParam}`}
                open={!!sourcesIntent}
                intent={sourcesIntent}
                entityType={objectEntityType}
                entityId={itemParam}
                lens={objectLense}
                onClose={() => setSourcesIntent(null)}
                onNavigate={handleNavigateToObject}
                onOpenGroup={(row) => {
                    setSourcesIntent(null);
                    setGroupRow(row);
                }}
            />
            <GroupEditorPanel
                key={`group:${objectEntityType}:${itemParam}`}
                open={!!groupRow}
                row={groupRow}
                entityType={objectEntityType}
                entityId={itemParam}
                lens={objectLense}
                onClose={() => setGroupRow(null)}
                onNavigate={handleNavigateToObject}
            />
            </>
        );
    };

    // --- Render helper ---
    const renderEntityList = () => {
        // While the primary load is in flight, render no entity content
        // so the EntityFeed's "No items" empty-state can't appear behind
        // the semi-transparent PageLoadOverlay (#680). This also covers
        // the bootstrap-loading window where ``entitiesLoading`` alone
        // would have been false.
        if (primaryLoadActive) {
            return null;
        }
        if (useLiveData && entitiesLoading) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '2rem', justifyContent: 'center', color: '#6b7280' }}>
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Daten werden geladen…</span>
                </div>
            );
        }
        // Only use grouped rendering when items actually span multiple groups;
        // EntityFeed silently drops items whose group doesn't match any key.
        const usedGroups = new Set(entityFeedItems.map(i => i.group).filter(Boolean));
        const effectiveGroups = activeGroups && usedGroups.size > 1 ? activeGroups : undefined;
        const emptyMessage = isAgreementView ? 'Keine Kontrakte vorhanden' : t('farm.noItems');
        // Issue #593 (2026-05-02): the Paginator only renders when the
        // server-reported total exceeds the page size (its internal
        // ``totalPages <= 1`` guard would also hide it, but checking
        // here keeps the chrome out of the demo path entirely). The
        // hook owns ``page`` state and resets it on lense / sub-lense /
        // filter change, so a sub-lense click never strands the user
        // on page 7 of the previous result set.
        const showPaginator = useLiveData && entitiesQuery.total > entitiesQuery.pageSize;
        return (
            <>
                {showYearAxis && (
                    <YearAxisControl
                        buckets={yearAxis}
                        activeYear={appliedYear}
                        onYearChange={handleYearChange}
                    />
                )}
                {ffFilter && (
                    <div className="farm-page__ff-chip">
                        <span>Gefiltert nach Verknüpfung</span>
                        <button
                            type="button"
                            className="farm-page__ff-clear"
                            onClick={clearFfFilter}
                            aria-label="Verknüpfungsfilter entfernen"
                        >
                            <X size={12} />
                        </button>
                    </div>
                )}
                <EntityFeed
                    key={`${effectiveCategory ?? ''}|${effectiveSubcategory ?? ''}|${entitiesQuery.page}|${ffParam ?? ''}|${appliedYear ?? ''}`}
                    items={entityFeedItems}
                    groups={effectiveGroups}
                    emptyMessage={emptyMessage}
                    onExpandedChange={handleExpandedChange}
                />
                {showPaginator && (
                    <Paginator
                        currentPage={entitiesQuery.page - 1}
                        pageSize={entitiesQuery.pageSize}
                        totalItems={entitiesQuery.total}
                        onPageChange={(zeroBasedPage) => entitiesQuery.setPage(zeroBasedPage + 1)}
                    />
                )}
            </>
        );
    };

    return (
        <>
            <PageLoadOverlay
                visible={pageLoadOverlay.showOverlay}
                elapsedSeconds={pageLoadOverlay.elapsedSeconds}
            />
            {!useLiveData && (
                <MockDataLabel
                    text={isAgreementView ? t('farm.contractsDemo') : t('farm.demoData')}
                    position="top-right"
                    color="blue"
                />
            )}
            <MyFarmPageLayout
                categories={farmCategories}
                selectedCategory={effectiveCategory ?? ''}
                selectedSubcategory={effectiveSubcategory}
                onCategorySelect={handleCategorySelect}
                rootLabel={t('farm.title')}
                lensePanelTitle={t('farm.panelTitle', 'Digital Twin')}
            >
                {showTermPage ? renderTermPage() : showObjectView ? renderObjectView() : renderEntityList()}
            </MyFarmPageLayout>

            <DocumentPreview
                isOpen={previewState.isOpen}
                documentUrl={previewState.documentUrl}
                filename={previewState.filename}
                onClose={handlePreviewClose}
                onDownload={handlePreviewDownload}
                onRotate={handlePreviewRotate}
                rotating={rotating}
                loading={previewState.loading}
                error={previewState.error}
                backLabel={t('documents.backToDocuments') || 'Zurück'}
                downloadLabel={t('documents.download') || 'Download'}
                printLabel={t('documents.print') || 'Drucken'}
                rotateLabel={t('documents.rotate') || 'Drehen'}
                closeLabel={t('common.close') || 'Schließen'}
                loadingLabel={t('documents.loadingDocument') || 'Dokument wird geladen...'}
            />
        </>
    );
};

export default FarmPage;
